import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, MapPin, DollarSign, UserCheck } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { categoryLabel } from "@/lib/catalog";
import { from } from "@/lib/cameFrom";
import { budgetText, jobLocationText } from "@/lib/jobDisplay";
import { formatDate } from "@/lib/dates";

export default function BrowseJobs() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ status: "open" });
    if (q) params.set("q", q);
    if (category) params.set("category_id", category);
    try {
      const { data } = await api.get(`/jobs?${params}`);
      setJobs(data.jobs || []);
    } finally { setLoading(false); }
  }, [q, category]);

  useEffect(() => {
    api.get("/catalog/categories")
      .then((r) => setCategories(r.data.categories || []))
      // DY2.2 V4: no loading flag hangs on this one, so a failure is an empty
      // filter rather than a stuck page — but it used to leave no trace at all.
      .catch((e) => console.warn("[BrowseJobs] categories failed to load", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  /**
   * A request sent to this FixyBee by name goes first, and says so.
   *
   * `GET /jobs` already only returns a directed job to the one person it names
   * (routes/jobs.py, `directed_provider_id.is_(None) | == user.id`), so a
   * non-null `directed_provider_id` on this board is always the reader's — the
   * comparison is belt and braces, and the same one JobDetail.jsx makes. Until
   * now the row was indistinguishable from the open demand around it: the push
   * said "a client asked YOU", and the board it linked to said nothing, buried
   * by whatever had been posted more recently.
   *
   * Sorted, not filtered into a section: the board stays one list, and within
   * each half the server's ordering (newest first) is preserved — `sort` is
   * stable in every engine we ship to.
   */
  const isForMe = (j) =>
    !!j.directed_provider_id && j.directed_provider_id === user?.user_id;
  const ordered = [...jobs].sort((a, b) => isForMe(b) - isForMe(a));

  return (
    <DashboardLayout role="provider">
      <div data-testid="browse-jobs-page">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">{t("browseJobs.title")}</h1>
        <p className="mt-1 text-smoke">{t("browseJobsX.subtitle")}</p>

        <div className="mt-6 grid sm:grid-cols-[1fr_240px] gap-3">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
            <input className="input-base pl-10" placeholder={t("browseJobsX.searchPh")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
          </form>
          <select className="input-base" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="category-filter">
            <option value="">{t("browseJobsX.filterAll")}</option>
            {categories.map((c) => <option key={c.category_id} value={c.category_id}>{categoryLabel(t, c)}</option>)}
          </select>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="space-y-3">{Array(3).fill(0).map((_, i) => <div key={i} className="skeleton h-32" />)}</div>
          ) : jobs.length === 0 ? (
            <div className="card-base p-10 text-center">
              <p className="text-smoke">{t("browseJobsX.empty")}</p>
            </div>
          ) : (
            <div className="grid gap-3" data-testid="browse-jobs-list">
              {ordered.map((j) => (
                <Link key={j.job_id} to={`/provider/jobs/${j.job_id}`}
                      state={from("/provider/browse")}
                      className="card-base p-5 block" data-testid={`browse-job-${j.job_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {isForMe(j) && (
                        <span
                          className="badge bg-worker text-hive inline-flex items-center gap-1 mb-2"
                          data-testid={`browse-job-directed-${j.job_id}`}
                        >
                          <UserCheck size={12} /> {t("browseJobsX.forYou")}
                        </span>
                      )}
                      <h3 className="font-display text-lg font-bold text-worker">{j.title}</h3>
                      <p className="text-sm text-smoke line-clamp-2 mt-1">{j.description}</p>
                      <div className="mt-3 flex gap-3 text-xs text-smoke font-mono flex-wrap">
                        <span className="flex items-center gap-1"><DollarSign size={12} />{budgetText(t, j.budget_min, j.budget_max)}</span>
                        {jobLocationText(j) && (
                          <span className="flex items-center gap-1"><MapPin size={12} />{jobLocationText(j)}</span>
                        )}
                        <span>{formatDate(j.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
